/**
 * API service for processing rules management.
 */

import { amplifyConfig } from "@/config/amplify";
import {
  CarrierListResponse,
  CustomerListResponse,
  ProcessingRule,
  RuleCreateData,
  RuleFilters,
  RuleListResponse,
  RuleUpdateData,
} from "@/types/rules";

const API_BASE_URL = amplifyConfig.apiUrl;

/**
 * Build query string from filters object
 */
function buildQueryString(filters?: RuleFilters): string {
  if (!filters) return "";

  const params = new URLSearchParams();

  if (filters.agent_type) params.append("agent_type", filters.agent_type);
  if (filters.is_active !== undefined) params.append("is_active", String(filters.is_active));
  if (filters.document_type) params.append("document_type", filters.document_type);
  if (filters.source_type) params.append("source_type", filters.source_type);
  if (filters.carrier_code) params.append("carrier_code", filters.carrier_code);
  if (filters.customer_ref_code) params.append("customer_ref_code", filters.customer_ref_code);
  if (filters.search) params.append("search", filters.search);
  if (filters.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters.offset !== undefined) params.append("offset", String(filters.offset));

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * List processing rules with optional filters
 */
export async function listRules(token: string, filters?: RuleFilters): Promise<RuleListResponse> {
  const queryString = buildQueryString(filters);
  const response = await fetch(`${API_BASE_URL}/v1/rules${queryString}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch rules: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single processing rule by ID
 */
export async function getRule(token: string, ruleId: number): Promise<ProcessingRule> {
  const response = await fetch(`${API_BASE_URL}/v1/rules/${ruleId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch rule: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new processing rule
 */
export async function createRule(token: string, data: RuleCreateData): Promise<ProcessingRule> {
  const response = await fetch(`${API_BASE_URL}/v1/rules`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to create rule: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing processing rule
 */
export async function updateRule(
  token: string,
  ruleId: number,
  data: RuleUpdateData
): Promise<ProcessingRule> {
  const response = await fetch(`${API_BASE_URL}/v1/rules/${ruleId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to update rule: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a processing rule
 */
export async function deleteRule(token: string, ruleId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/rules/${ruleId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete rule: ${response.statusText}`);
  }
}

/**
 * Search carriers for dropdown population
 */
export async function searchCarriers(token: string, search?: string): Promise<CarrierListResponse> {
  const queryString = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await fetch(`${API_BASE_URL}/v1/carriers${queryString}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to search carriers: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Search customers for dropdown population
 */
export async function searchCustomers(
  token: string,
  search?: string
): Promise<CustomerListResponse> {
  const queryString = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await fetch(`${API_BASE_URL}/v1/customers${queryString}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to search customers: ${response.statusText}`);
  }

  return response.json();
}
